// Managing company-wide non-working days (round 22).
//
// ZT's page, reimplemented: the weekday toggles up top (dirty-gated save, so
// an idle visit writes nothing), the named dated entries below — a stacked
// card list on phones, a table from `sm` up. Kind chips are amber, matching
// the calendar's dated-day shading.
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  scheduling,
  type NonWorkingDay,
  type NonWorkingDayKind,
} from '../schedulingApi';
import { ApiError } from '../api';
import { dateRangeLabel, WEEKDAY_LABELS } from '../lib/dates';
import { CardList, RowCard } from '../components/ui/RowCard';
import { SortHeader } from '../components/ui/SortHeader';
import { sortRows, useSort } from '../lib/sortRows';
import { Modal } from '../components/ui/Modal';

type SortKey = 'name' | 'date' | 'kind';

const KIND_LABELS: Record<NonWorkingDayKind, string> = {
  public_holiday: 'Public holiday',
  rdo: 'RDO',
  shutdown: 'Shutdown',
  other: 'Other',
};

// Chips render Monday-first (the board's order) but store JS getDay()
// numbers, matching the server and every Date in the browser.
const WEEKDAY_CHIPS = [1, 2, 3, 4, 5, 6, 0].map((dow, index) => ({
  dow,
  label: WEEKDAY_LABELS[index]!,
}));

export function NonWorkingDaysPage() {
  const [weekdays, setWeekdays] = useState<number[]>([]);
  const [savedWeekdays, setSavedWeekdays] = useState<number[]>([]);
  const [days, setDays] = useState<NonWorkingDay[]>([]);
  // By date, because the question this page answers is "what is coming up".
  const { sortKey, sortDir, onSort } = useSort<SortKey>('date');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<NonWorkingDay | 'new' | null>(null);

  useEffect(() => {
    void scheduling
      .nonWorkingDays()
      .then((result) => {
        setWeekdays(result.weekdays);
        setSavedWeekdays(result.weekdays);
        setDays(result.days);
      })
      .catch(() => setError('Could not load the non-working days.'))
      .finally(() => setLoading(false));
  }, []);

  const dirty = useMemo(
    () => JSON.stringify([...weekdays].sort()) !== JSON.stringify([...savedWeekdays].sort()),
    [weekdays, savedWeekdays]
  );

  const visible = useMemo(
    () =>
      sortRows(
        days,
        (day) => {
          switch (sortKey) {
            case 'name':
              return day.name;
            case 'date':
              return day.startDate;
            case 'kind':
              return KIND_LABELS[day.kind];
          }
        },
        sortDir
      ),
    [days, sortKey, sortDir]
  );

  async function saveWeekdays() {
    setBusy(true);
    setError(null);
    try {
      const result = await scheduling.saveNonWorkingWeekdays(weekdays);
      setSavedWeekdays(result.weekdays);
      setWeekdays(result.weekdays);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save the weekdays.');
    } finally {
      setBusy(false);
    }
  }

  async function remove(day: NonWorkingDay) {
    if (!window.confirm(`Remove ${day.name}? The schedule stops shading those days.`)) return;
    setBusy(true);
    try {
      await scheduling.deleteNonWorkingDay(day.id);
      setDays((current) => current.filter((d) => d.id !== day.id));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not remove that entry.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="max-w-3xl">
      <Link to="/app/calendar" className="row-action -ml-2">
        ‹ Back to the schedule
      </Link>
      <h1 className="mb-1 mt-2 text-2xl font-bold text-gray-900">Non-working days</h1>
      <p className="mb-6 text-sm text-gray-600">
        The schedule shades these days and bookings skip over them, unless a booking sits
        entirely on non-working days, which stays visible because somebody meant it.
      </p>

      {error && (
        <div className="banner-error mb-4" role="alert">
          {error}
        </div>
      )}

      {loading ? (
        <div className="card px-5 py-8 text-center text-sm text-gray-500">Loading…</div>
      ) : (
        <>
          <section className="card mb-6 p-5">
            <h2 className="mb-1 text-sm font-semibold text-gray-900">Weekly days off</h2>
            <p className="mb-3 text-sm text-gray-600">
              Which days of the week the company doesn&apos;t work. Most companies keep the
              weekend.
            </p>
            <div className="flex flex-wrap gap-2">
              {WEEKDAY_CHIPS.map(({ dow, label }) => {
                const on = weekdays.includes(dow);
                return (
                  <button
                    key={dow}
                    type="button"
                    aria-pressed={on}
                    onClick={() =>
                      setWeekdays((current) =>
                        on ? current.filter((d) => d !== dow) : [...current, dow]
                      )
                    }
                    className={`min-h-[44px] rounded-md border px-3 py-2.5 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-brand-600 sm:min-h-0 sm:py-1.5 ${
                      on
                        ? 'border-brand-500 bg-brand-500 text-ink'
                        : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            {dirty && (
              <div className="mt-3">
                <button
                  type="button"
                  className="btn-primary btn-sm"
                  onClick={() => void saveWeekdays()}
                  disabled={busy}
                >
                  {busy ? 'Saving…' : 'Save weekdays'}
                </button>
              </div>
            )}
          </section>

          <section>
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-900">
                Holidays, RDOs and shutdowns
              </h2>
              <button type="button" className="btn-primary btn-sm" onClick={() => setEditing('new')}>
                Add a day
              </button>
            </div>

            {days.length === 0 ? (
              <div className="card px-5 py-8 text-center text-sm text-gray-500">
                Nothing yet. Public holidays are the usual first entries.
              </div>
            ) : (
              <>
                <CardList>
                  {visible.map((day) => (
                    <RowCard
                      key={day.id}
                      title={day.name}
                      badges={<KindChip kind={day.kind} />}
                      actions={
                        <>
                          <button type="button" className="row-action" onClick={() => setEditing(day)}>
                            Edit
                          </button>
                          <button
                            type="button"
                            className="row-action-danger"
                            onClick={() => void remove(day)}
                          >
                            Remove
                          </button>
                        </>
                      }
                    >
                      <div>{dateRangeLabel(day.startDate, day.endDate)}</div>
                      {day.notes && <div>{day.notes}</div>}
                    </RowCard>
                  ))}
                </CardList>

                {/* `table-wrap max-sm:hidden` and `.table-cell`, like every
                    other list in the app — this one had grown its own wrapper
                    and hand-written cell padding, which meant it did not match
                    the card/table pairing the shared test helper looks for. */}
                <div className="table-wrap max-sm:hidden">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <SortHeader label="Name" sortKey="name" current={sortKey} dir={sortDir} onSort={onSort} />
                        <SortHeader label="Date(s)" sortKey="date" current={sortKey} dir={sortDir} onSort={onSort} />
                        <SortHeader label="Type" sortKey="kind" current={sortKey} dir={sortDir} onSort={onSort} />
                        <th scope="col" className="table-header">
                          Notes
                        </th>
                        <th className="w-px" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {visible.map((day) => (
                        <tr key={day.id} className="hover:bg-gray-50">
                          <td className="table-cell font-medium text-gray-900">{day.name}</td>
                          <td className="table-cell table-muted">
                            {dateRangeLabel(day.startDate, day.endDate)}
                          </td>
                          <td className="table-cell">
                            <KindChip kind={day.kind} />
                          </td>
                          <td className="table-cell table-muted">{day.notes}</td>
                          <td className="table-cell whitespace-nowrap text-right">
                            <button type="button" className="row-action" onClick={() => setEditing(day)}>
                              Edit
                            </button>
                            <button
                              type="button"
                              className="row-action-danger"
                              onClick={() => void remove(day)}
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </section>
        </>
      )}

      {editing && (
        <DayModal
          day={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(saved) => {
            setDays((current) => {
              const rest = current.filter((d) => d.id !== saved.id);
              return [...rest, saved].sort((a, b) => a.startDate.localeCompare(b.startDate));
            });
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function KindChip({ kind }: { kind: NonWorkingDayKind }) {
  return (
    <span className="inline-block rounded-full border border-amber-200 bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
      {KIND_LABELS[kind]}
    </span>
  );
}

function DayModal({
  day,
  onClose,
  onSaved,
}: {
  day: NonWorkingDay | null;
  onClose: () => void;
  onSaved: (day: NonWorkingDay) => void;
}) {
  const [name, setName] = useState(day?.name ?? '');
  const [startDate, setStartDate] = useState(day?.startDate ?? '');
  const [endDate, setEndDate] = useState(day?.endDate ?? '');
  const [kind, setKind] = useState<NonWorkingDayKind>(day?.kind ?? 'public_holiday');
  const [notes, setNotes] = useState(day?.notes ?? '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    const body = {
      name,
      startDate,
      endDate,
      kind,
      notes: notes.trim() === '' ? null : notes.trim(),
    };
    try {
      const result = day
        ? await scheduling.updateNonWorkingDay(day.id, body)
        : await scheduling.createNonWorkingDay(body);
      onSaved(result.day);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save that.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={day ? 'Edit non-working day' : 'Add a non-working day'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn-ghost btn-sm" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="submit"
            form="nw-day-form"
            className="btn-primary btn-sm"
            disabled={busy || name.trim() === '' || startDate === '' || endDate === ''}
          >
            {busy ? 'Saving…' : day ? 'Save changes' : 'Add it'}
          </button>
        </>
      }
    >
      {error && (
        <div className="banner-error mb-3" role="alert">
          {error}
        </div>
      )}
      <form id="nw-day-form" onSubmit={submit} noValidate className="space-y-4">
        <div>
          <label className="form-label" htmlFor="nw-name">
            Name
          </label>
          <input
            id="nw-name"
            className="form-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Christmas Day"
          />
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className="form-label" htmlFor="nw-start">
              From
            </label>
            <input
              id="nw-start"
              type="date"
              className="form-input"
              value={startDate}
              onChange={(e) => {
                const value = e.target.value;
                setStartDate(value);
                if (endDate === '' || endDate < value) setEndDate(value);
              }}
            />
          </div>
          <div>
            <label className="form-label" htmlFor="nw-end">
              To
            </label>
            <input
              id="nw-end"
              type="date"
              className="form-input"
              min={startDate || undefined}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>
        <div>
          <label className="form-label" htmlFor="nw-kind">
            Type
          </label>
          <select
            id="nw-kind"
            className="form-select"
            value={kind}
            onChange={(e) => setKind(e.target.value as NonWorkingDayKind)}
          >
            {(Object.keys(KIND_LABELS) as NonWorkingDayKind[]).map((k) => (
              <option key={k} value={k}>
                {KIND_LABELS[k]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="form-label" htmlFor="nw-notes">
            Notes
          </label>
          <input
            id="nw-notes"
            className="form-input"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>
      </form>
    </Modal>
  );
}
